import { writable, derived, get } from "svelte/store";
import { db } from "./db/index.js";
import axios, { axiosWithCsrfRetry } from "@edu-quizz/shared/lib/axios";

/**
 * Authentication store
 */
function createAuthStore() {
    const { subscribe, set, update } = writable({
        isAuthenticated: false,
        code: null,
        user: null,
        loading: true,
    });

    return {
        subscribe,
        /**
         * Initialize auth from database
         */
        async init() {
            try {
                const auth = await db.getAuth();
                console.log("Auth init - data from DB:", auth);
                if (auth) {
                    set({
                        isAuthenticated: true,
                        code: auth.code,
                        user: auth.name
                            ? { name: auth.name }
                            : null,
                        loading: false,
                    });
                    console.log("Auth init - user is authenticated", { name: auth.name });
                } else {
                    set({
                        isAuthenticated: false,
                        code: null,
                        user: null,
                        loading: false,
                    });
                    console.log("Auth init - no authentication found");
                }
            } catch (error) {
                console.error("Failed to initialize auth:", error);
                set({
                    isAuthenticated: false,
                    code: null,
                    user: null,
                    loading: false,
                });
            }
        },
        /**
         * Login with code
         */
        async login(code, user) {
            try {
                await db.saveAuth(code, user);
                set({
                    isAuthenticated: true,
                    code,
                    user,
                    loading: false,
                });
                return true;
            } catch (error) {
                console.error("Login failed:", error);
                return false;
            }
        },
        /**
         * Logout
         */
        async logout() {
            try {
                // Call API to invalidate session on server
                try {
                    await axiosWithCsrfRetry(() => axios.post("/api/auth/logout"));
                    console.log("Server logout successful");
                } catch (apiError) {
                    console.error("Server logout failed:", apiError);
                    // Continue with local cleanup even if API call fails
                }

                // Clear local database
                await db.clearAll();

                // Update store state
                set({
                    isAuthenticated: false,
                    code: null,
                    user: null,
                    loading: false,
                });

                return true;
            } catch (error) {
                console.error("Logout failed:", error);
                return false;
            }
        },
    };
}

export const auth = createAuthStore();

/**
 * Network status store
 */
function createNetworkStore() {
    const { subscribe, set } = writable(navigator.onLine);

    // Listen for online/offline events
    if (typeof window !== "undefined") {
        window.addEventListener("online", () => set(true));
        window.addEventListener("offline", () => set(false));
    }

    return {
        subscribe,
    };
}

export const isOnline = createNetworkStore();

/**
 * Sync status store
 */
function createSyncStore() {
    const { subscribe, set, update } = writable({
        isSyncing: false,
        lastSync: null,
        pendingCount: 0,
        error: null,
        assetCaching: {
            inProgress: false,
            current: 0,
            total: 0,
        },
    });

    return {
        subscribe,
        /**
         * Set syncing status
         */
        setSyncing(syncing) {
            update((state) => ({ ...state, isSyncing: syncing, error: null }));
        },
        /**
         * Set sync completed
         */
        setSyncCompleted() {
            update((state) => ({
                ...state,
                isSyncing: false,
                lastSync: new Date(),
                error: null,
                assetCaching: {
                    inProgress: false,
                    current: 0,
                    total: 0,
                },
            }));
        },
        /**
         * Set sync error
         */
        setSyncError(error) {
            update((state) => ({
                ...state,
                isSyncing: false,
                error,
                assetCaching: {
                    inProgress: false,
                    current: 0,
                    total: 0,
                },
            }));
        },
        /**
         * Update pending count
         */
        async updatePendingCount() {
            try {
                const items = await db.getPendingSyncItems();
                update((state) => ({ ...state, pendingCount: items.length }));
            } catch (error) {
                console.error("Failed to update pending count:", error);
            }
        },
        /**
         * Set asset caching progress
         */
        setAssetCachingProgress(current, total) {
            update((state) => ({
                ...state,
                assetCaching: {
                    inProgress: true,
                    current,
                    total,
                },
            }));
        },
    };
}

export const syncStatus = createSyncStore();

/**
 * Resources store
 */
function createResourcesStore() {
    const { subscribe, set } = writable([]);

    return {
        subscribe,
        /**
         * Load resources from database
         */
        async load() {
            try {
                const resources = await db.getSchoolsHierarchy();
                set(resources);
            } catch (error) {
                console.error("Failed to load resources:", error);
                set([]);
            }
        },
        /**
         * Refresh resources (fetch from server and save to DB)
         */
        async refresh() {
            try {
                // Fetch offline resources from Laravel backend
                const response = await axios.get(
                    "/api/v1/student/offline-resources",
                );

                const data = response.data;
                await db.saveOfflineResources(data);
                await this.load();
                return true;
            } catch (error) {
                console.error("Failed to refresh resources:", error);
                return false;
            }
        },
    };
}

export const resources = createResourcesStore();

/**
 * Progress store
 */
function createProgressStore() {
    const { subscribe, set } = writable({});

    return {
        subscribe,
        /**
         * Load progress from database
         */
        async load() {
            try {
                const progressArray = await db.getAllProgress();
                // Convert array to map for easier lookup
                const progressMap = {};
                progressArray.forEach((p) => {
                    const key = `${p.resourceId}-${p.type}`;
                    progressMap[key] = p;
                });
                set(progressMap);
            } catch (error) {
                console.error("Failed to load progress:", error);
                set({});
            }
        },
        /**
         * Save progress for a resource
         */
        async save(resourceId, type, completed, score) {
            try {
                await db.saveProgress(resourceId, type, completed, score);
                await this.load();
                // Trigger sync count update
                await syncStatus.updatePendingCount();
            } catch (error) {
                console.error("Failed to save progress:", error);
            }
        },
        /**
         * Get progress for a resource
         */
        get(resourceId, type) {
            const state = get(this);
            const key = `${resourceId}-${type}`;
            return state[key] || null;
        },
    };
}

export const progress = createProgressStore();

/**
 * PWA install prompt — captured globally so it's never missed
 */
export const pwaInstallPrompt = writable(null);

if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        pwaInstallPrompt.set(e);
    });
}

/**
 * App installation status
 */
export const isInstalled = writable(false);

// Check if app is installed
if (typeof window !== "undefined") {
    const checkInstalled = () => {
        const isStandalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        isInstalled.set(isStandalone);
    };

    checkInstalled();

    // Listen for display mode changes
    window
        .matchMedia("(display-mode: standalone)")
        .addEventListener("change", checkInstalled);
}

/**
 * Derived store for overall app status
 */
export const appStatus = derived(
    [auth, isOnline, syncStatus],
    ([$auth, $isOnline, $syncStatus]) => ({
        isReady: $auth.isAuthenticated && !$auth.loading,
        isOnline: $isOnline,
        canSync: $isOnline && $auth.isAuthenticated,
        hasPendingSync: $syncStatus.pendingCount > 0,
        isSyncing: $syncStatus.isSyncing,
    }),
);

/**
 * Push notifications store
 */
function createPushNotificationsStore() {
    const { subscribe, set, update } = writable({
        supported: false,
        permission: "default",
        subscribed: false,
        loading: false,
        error: null,
    });

    return {
        subscribe,
        /**
         * Initialize push notifications
         */
        async init(pushManager) {
            const supported = pushManager.isSupported();

            if (!supported) {
                set({
                    supported: false,
                    permission: "default",
                    subscribed: false,
                    loading: false,
                    error: null,
                });
                return false;
            }

            try {
                const permission = pushManager.getPermissionStatus();
                const subscribed = await pushManager.isSubscribed();

                set({
                    supported: true,
                    permission,
                    subscribed,
                    loading: false,
                    error: null,
                });

                return true;
            } catch (error) {
                console.error(
                    "Failed to initialize push notifications store:",
                    error,
                );
                set({
                    supported: true,
                    permission: "default",
                    subscribed: false,
                    loading: false,
                    error: error.message,
                });
                return false;
            }
        },
        /**
         * Update status
         */
        async updateStatus(pushManager) {
            try {
                const permission = pushManager.getPermissionStatus();
                const subscribed = await pushManager.isSubscribed();

                update((state) => ({
                    ...state,
                    permission,
                    subscribed,
                    error: null,
                }));
            } catch (error) {
                console.error(
                    "Failed to update push notification status:",
                    error,
                );
                update((state) => ({
                    ...state,
                    error: error.message,
                }));
            }
        },
        /**
         * Set loading state
         */
        setLoading(loading) {
            update((state) => ({ ...state, loading }));
        },
        /**
         * Set error
         */
        setError(error) {
            update((state) => ({ ...state, error, loading: false }));
        },
        /**
         * Clear error
         */
        clearError() {
            update((state) => ({ ...state, error: null }));
        },
    };
}

export const pushNotificationsStore = createPushNotificationsStore();

/**
 * Theme store - manages color theme configuration
 */
function createThemeStore() {
    // Default black and white theme
    const defaultTheme = {
        primaryColorStart: '#1f2937',
        primaryColorEnd: '#111827',
        accentColorStart: '#374151',
        accentColorEnd: '#1f2937',
    };

    const { subscribe, set, update } = writable(defaultTheme);

    return {
        subscribe,
        /**
         * Initialize theme from database
         */
        async init() {
            try {
                const savedTheme = await db.getTheme();
                if (savedTheme) {
                    set({
                        primaryColorStart: savedTheme.primaryColorStart,
                        primaryColorEnd: savedTheme.primaryColorEnd,
                        accentColorStart: savedTheme.accentColorStart,
                        accentColorEnd: savedTheme.accentColorEnd,
                    });
                    this.applyThemeToDOM(savedTheme);
                } else {
                    // Save default theme to database
                    await db.saveTheme(
                        defaultTheme.primaryColorStart,
                        defaultTheme.primaryColorEnd,
                        defaultTheme.accentColorStart,
                        defaultTheme.accentColorEnd
                    );
                    this.applyThemeToDOM(defaultTheme);
                }
            } catch (error) {
                console.error('Failed to initialize theme:', error);
                set(defaultTheme);
                this.applyThemeToDOM(defaultTheme);
            }
        },
        /**
         * Update theme colors
         */
        async updateTheme(primaryColorStart, primaryColorEnd, accentColorStart, accentColorEnd) {
            try {
                const newTheme = {
                    primaryColorStart,
                    primaryColorEnd,
                    accentColorStart,
                    accentColorEnd,
                };

                await db.updateTheme(newTheme);
                set(newTheme);
                this.applyThemeToDOM(newTheme);

                return true;
            } catch (error) {
                console.error('Failed to update theme:', error);
                return false;
            }
        },
        /**
         * Reset to default theme
         */
        async resetToDefault() {
            return await this.updateTheme(
                defaultTheme.primaryColorStart,
                defaultTheme.primaryColorEnd,
                defaultTheme.accentColorStart,
                defaultTheme.accentColorEnd
            );
        },
        /**
         * Apply theme colors to DOM using CSS custom properties
         */
        applyThemeToDOM(theme) {
            if (typeof document !== 'undefined') {
                const root = document.documentElement;
                root.style.setProperty('--primary-gradient-start', theme.primaryColorStart);
                root.style.setProperty('--primary-gradient-end', theme.primaryColorEnd);
                root.style.setProperty('--accent-gradient-start', theme.accentColorStart);
                root.style.setProperty('--accent-gradient-end', theme.accentColorEnd);
            }
        },
        /**
         * Get current theme
         */
        getCurrent() {
            return get(this);
        },
    };
}

export const theme = createThemeStore();

/**
 * Theme store - manages DaisyUI theme preference
 * Available themes: lofi (default), dark (prefersdark), nord, wireframe
 */
function createThemePreferenceStore() {
    const availableThemes = [
        { id: 'lofi', label: 'Lofi', isDefault: true },
        { id: 'dark', label: 'Dark', prefersDark: true },
        { id: 'nord', label: 'Nord' },
        { id: 'wireframe', label: 'Wireframe' }
    ];

    const { subscribe, set } = writable('lofi');

    return {
        subscribe,
        availableThemes,
        /**
         * Initialize theme from database or system preference
         */
        async init() {
            try {
                const savedMode = await db.getDarkMode();
                // Migration: if we have a boolean saved, convert to theme name
                if (typeof savedMode === 'boolean') {
                    const theme = savedMode ? 'dark' : 'lofi';
                    await db.saveDarkMode(theme);
                    set(theme);
                    this.applyTheme(theme);
                } else if (savedMode && typeof savedMode === 'string') {
                    set(savedMode);
                    this.applyTheme(savedMode);
                } else {
                    // Check system preference
                    const prefersDark = window.matchMedia &&
                        window.matchMedia('(prefers-color-scheme: dark)').matches;
                    const theme = prefersDark ? 'dark' : 'lofi';
                    set(theme);
                    this.applyTheme(theme);
                    await db.saveDarkMode(theme);
                }
            } catch (error) {
                console.error('Failed to initialize theme:', error);
                set('lofi');
                this.applyTheme('lofi');
            }
        },
        /**
         * Set theme
         */
        async setTheme(themeName) {
            try {
                // Validate theme exists
                if (!availableThemes.find(t => t.id === themeName)) {
                    console.error('Invalid theme:', themeName);
                    return false;
                }

                await db.saveDarkMode(themeName);
                set(themeName);
                this.applyTheme(themeName);
                return true;
            } catch (error) {
                console.error('Failed to set theme:', error);
                return false;
            }
        },
        /**
         * Toggle between themes (cycles through available themes)
         */
        async toggle() {
            const currentTheme = get(this);
            const currentIndex = availableThemes.findIndex(t => t.id === currentTheme);
            const nextIndex = (currentIndex + 1) % availableThemes.length;
            const nextTheme = availableThemes[nextIndex].id;

            return await this.setTheme(nextTheme);
        },
        /**
         * Apply theme to DOM
         */
        applyTheme(themeName) {
            if (typeof document !== 'undefined') {
                document.documentElement.setAttribute('data-theme', themeName);
            }
        },
    };
}

export const darkMode = createThemePreferenceStore();

/**
 * Font preference store - manages accessible font selection
 * Available fonts: default (system), arial, verdana, tahoma
 */
function createFontPreferenceStore() {
    const availableFonts = [
        { id: 'default', label: 'Par défaut', fontFamily: null },
        { id: 'arial', label: 'Arial', fontFamily: 'Arial, sans-serif' },
        { id: 'verdana', label: 'Verdana', fontFamily: 'Verdana, sans-serif' },
        { id: 'tahoma', label: 'Tahoma', fontFamily: 'Tahoma, sans-serif' }
    ];

    const { subscribe, set } = writable('default');

    return {
        subscribe,
        availableFonts,
        /**
         * Initialize font preference from database
         */
        async init() {
            try {
                const savedFont = await db.getFontPreference();
                if (savedFont && typeof savedFont === 'string') {
                    // Validate font exists
                    if (availableFonts.find(f => f.id === savedFont)) {
                        set(savedFont);
                        this.applyFont(savedFont);
                    } else {
                        // Invalid saved font, use default
                        set('default');
                        this.applyFont('default');
                        await db.saveFontPreference('default');
                    }
                } else {
                    // No saved preference, use default
                    set('default');
                    this.applyFont('default');
                    await db.saveFontPreference('default');
                }
            } catch (error) {
                console.error('Failed to initialize font preference:', error);
                set('default');
                this.applyFont('default');
            }
        },
        /**
         * Set font preference
         */
        async setFont(fontId) {
            try {
                // Validate font exists
                if (!availableFonts.find(f => f.id === fontId)) {
                    console.error('Invalid font:', fontId);
                    return false;
                }

                await db.saveFontPreference(fontId);
                set(fontId);
                this.applyFont(fontId);
                return true;
            } catch (error) {
                console.error('Failed to set font:', error);
                return false;
            }
        },
        /**
         * Apply font to DOM
         */
        applyFont(fontId) {
            if (typeof document !== 'undefined') {
                const font = availableFonts.find(f => f.id === fontId);
                if (font) {
                    if (font.fontFamily) {
                        document.documentElement.style.setProperty('font-family', font.fontFamily);
                    } else {
                        // Remove custom font to restore default
                        document.documentElement.style.removeProperty('font-family');
                    }
                }
            }
        },
    };
}

export const fontPreference = createFontPreferenceStore();
